/**
 * P7 — N3 · task-model H3.7.K01
 *
 *   "Marque os pontos de coordenadas inteiras que estão dentro da figura."
 *
 * TRÊS DECISÕES, cada uma acrescentando uma escolha que o aluno precisa fazer:
 *
 * 1. **A MALHA É QUADRICULADA, MAS NÃO É NUMERADA.** Os quadradinhos mostram onde se
 *    pode marcar — sem eles, marcar vira teste de pontaria. Mas só o primeiro
 *    cruzamento depois da origem, em cada eixo, diz quanto mede o lado do
 *    quadradinho. Descobrir quais cruzamentos têm coordenadas inteiras continua sendo
 *    trabalho do aluno, que é o objeto do item.
 *
 * 2. **O QUADRADINHO PODE MEDIR MENOS QUE 1.** Em parte das variantes o lado é 1/2 ou
 *    1/3. A marcação no eixo diz "1/2", e não "1": o aluno lê o tamanho do menor
 *    segmento e precisa deduzir que são dois quadradinhos por unidade. Não basta marcar
 *    tudo o que cabe na figura — é preciso separar os cruzamentos de coordenadas
 *    INTEIRAS dos demais.
 *
 * 3. **A FIGURA É IRREGULAR E TORTA.** Polígono de 4 a 6 lados, sem alinhamento com
 *    os eixos, o que impede resolver "de cabeça" por simetria.
 *
 * FRONTEIRA: o gerador CONSTRÓI a figura de modo que nenhum ponto inteiro fique perto
 * do contorno. Não existe caso de borda para o aluno julgar, e o enunciado pode dizer
 * "dentro da figura" sem letra miúda. Cruzamentos NÃO inteiros podem cair sobre o
 * contorno — mas esses nunca fazem parte da resposta, e o enunciado só promete sobre
 * os inteiros.
 *
 * SOBRE A HABILIDADE: `H3.7` (corresponder pontos do plano cartesiano a coordenadas)
 * continua sendo o parentesco mais próximo, mas a leitura de escala empurra este item
 * para além do recorte original. É candidato natural a uma habilidade própria no
 * catálogo `K`, quando ele for escrito.
 */

import { gcd } from '../../core/num.ts';
import { adjetivo, capitalizar, conta, numeroDe, verbo } from '../../core/pt.ts';
import type { Rng } from '../../core/rng.ts';
import { sampleWhere } from '../../core/sampling.ts';
import type { ConstructionSnapshot, GeoId, GeoObject, GPoint } from '../../geom/construction.ts';
import { EMPTY_SNAPSHOT } from '../../geom/construction.ts';
import { latticeSetEquals } from '../../geom/predicates.ts';
import { pointInPolygon } from '../../geom/primitives.ts';
import type { Tol } from '../../geom/tolerance.ts';
import type { Vec2 } from '../../geom/vec.ts';
import type { Rect, Shape } from '../../render/scene.ts';
import { scene } from '../../render/scene.ts';
import type { DifficultySourceCode } from '../../schema/difficulty.ts';
import { MALHA_ESCALA_IGNORADA, MALHA_INCLUI_FORA, MALHA_INCOMPLETO } from '../../schema/errors.ts';
import type { ConstructionTarget, ItemTemplate } from '../../schema/item.ts';
import { templateId } from '../../schema/item.ts';
import { br, strong, txt, type Inline } from '../../schema/richtext.ts';
import { skillCode, taskModelCode } from '../../schema/taxonomy.ts';

/** Unidades de desenho por unidade matemática. */
export const U = 46;

/** Nada no DAG: a figura é cenário, e o aluno começa com a prancheta limpa. */
const GIVEN_IDS: ReadonlySet<string> = new Set<string>();

/** Denominador do lado do quadradinho: 1 = lado 1; 2 = lado 1/2; 3 = lado 1/3. */
type Denominador = 1 | 2 | 3;

export interface Params {
  /** Vértices do polígono, em coordenadas matemáticas. */
  readonly vertices: readonly Vec2[];
  readonly denominador: Denominador;
  /** Pontos de coordenadas inteiras dentro da figura. */
  readonly resposta: readonly Vec2[];
  /** Quantos cruzamentos da malha existem dentro (inclui os não inteiros). */
  readonly marcaveisDentro: number;
}

const emTela = (v: Vec2): Vec2 => ({ x: v.x * U, y: v.y * U });

/**
 * Sorteia a figura. O polígono vem de `construirPoligono`, que garante a margem por
 * aritmética; aqui só se confere que o número de pontos-resposta é razoável — entre 4
 * e 10, o suficiente para exigir método sem virar maratona de cliques.
 */
function drawParams(rng: Rng): Params {
  const denominador = rng.pick([1, 2, 2, 3]) as Denominador;
  const escala = 1 / denominador;

  const vertices = sampleWhere(
    rng,
    (r) => construirPoligono(r),
    (v) => v !== null && inteirosDentro(v).length >= 4 && inteirosDentro(v).length <= 10,
    { label: 'poligono', maxTries: 200 },
  ) as Vec2[];

  return {
    vertices,
    denominador,
    resposta: inteirosDentro(vertices),
    marcaveisDentro: contarMarcaveis(vertices, escala),
  };
}

/**
 * MARGEM GARANTIDA POR CONSTRUÇÃO, não por sorteio.
 *
 * A primeira tentativa sorteava polígonos e rejeitava os que passassem perto de um
 * ponto da malha. Não funcionava, e não por azar: uma aresta de 4 unidades cruzando
 * uma malha de passo 1/3 passa perto de algum ponto quase sempre. A taxa de rejeição
 * deu 100% — o espaço não tinha o que amostrar.
 *
 * A saída é escolher cada aresta SABENDO onde estão os pontos. Para uma direção u, a
 * distância de um ponto P à reta de normal u e deslocamento d é |P·u − d|. Então basta
 * projetar todos os pontos inteiros candidatos sobre u, ordenar, e escolher d no MEIO
 * DE UM VÃO grande dessa lista. A margem deixa de ser esperança e vira aritmética.
 *
 * O polígono é a interseção dos semiplanos assim escolhidos, recortada de um quadrado
 * grande (Sutherland–Hodgman). Sai convexo, torto e sem alinhamento com os eixos.
 */
const MARGEM_PROJ = 0.2;
const ALCANCE = 5;

function construirPoligono(rng: Rng): Vec2[] | null {
  const k = rng.int(4, 5);
  const giro = rng.float(0, Math.PI * 2);

  // Candidatos: todos os pontos inteiros ao alcance.
  let dentro: Vec2[] = [];
  for (let i = -ALCANCE; i <= ALCANCE; i++) {
    for (let j = -ALCANCE; j <= ALCANCE; j++) dentro.push({ x: i, y: j });
  }

  let poligono: Vec2[] = [
    { x: -ALCANCE - 1, y: -ALCANCE - 1 },
    { x: ALCANCE + 1, y: -ALCANCE - 1 },
    { x: ALCANCE + 1, y: ALCANCE + 1 },
    { x: -ALCANCE - 1, y: ALCANCE + 1 },
  ];

  for (let m = 0; m < k; m++) {
    // Direções espalhadas, com desvio: nunca paralelas aos eixos. Se uma direção não
    // oferecer vão utilizável, tenta-se outro desvio antes de desistir — desistir do
    // polígono inteiro por causa de UMA direção era o que enchia a taxa de rejeição.
    let aplicou = false;

    for (let tentativa = 0; tentativa < 8 && !aplicou; tentativa++) {
      const ang = giro + (2 * Math.PI * m) / k + rng.float(-0.35, 0.35);
      const u = { x: Math.cos(ang), y: Math.sin(ang) };

      const proj = dentro.map((p) => p.x * u.x + p.y * u.y).sort((a, b) => a - b);

      // Vãos largos o bastante para acomodar a margem dos dois lados.
      const cortes: number[] = [];
      for (let t = 0; t < proj.length - 1; t++) {
        const a = proj[t] as number;
        const b = proj[t + 1] as number;
        if (b - a < 2 * MARGEM_PROJ) continue;
        const ficam = t + 1;
        // Cortes extremos dão figura vazia, ou figura que não corta nada.
        if (ficam >= dentro.length * 0.25 && ficam <= dentro.length * 0.95) {
          cortes.push((a + b) / 2);
        }
      }
      if (cortes.length === 0) continue;

      const d = rng.pick(cortes);
      const restantes = dentro.filter((p) => p.x * u.x + p.y * u.y < d);
      const recortado = recortar(poligono, u, d);
      if (recortado.length < 3) continue;

      dentro = restantes;
      poligono = recortado;
      aplicou = true;
    }

    if (!aplicou) return null;
  }

  return poligono.length >= 3 ? poligono : null;
}

/** Sutherland–Hodgman: recorta o polígono pelo semiplano P·u <= d. */
function recortar(pts: readonly Vec2[], u: Vec2, d: number): Vec2[] {
  const out: Vec2[] = [];
  const f = (p: Vec2): number => p.x * u.x + p.y * u.y - d;

  for (let i = 0; i < pts.length; i++) {
    const a = pts[i] as Vec2;
    const b = pts[(i + 1) % pts.length] as Vec2;
    const fa = f(a);
    const fb = f(b);

    if (fa <= 0) out.push(a);
    if (fa * fb < 0) {
      const t = fa / (fa - fb);
      out.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
    }
  }
  return out;
}

/** Pontos de coordenadas inteiras dentro do polígono. */
export function inteirosDentro(vertices: readonly Vec2[]): Vec2[] {
  const out: Vec2[] = [];
  const [x0, x1, y0, y1] = limites(vertices);
  for (let x = Math.ceil(x0); x <= Math.floor(x1); x++) {
    for (let y = Math.ceil(y0); y <= Math.floor(y1); y++) {
      if (pointInPolygon({ x, y }, vertices, 0)) out.push({ x, y });
    }
  }
  return out;
}

function contarMarcaveis(vertices: readonly Vec2[], escala: number): number {
  let n = 0;
  const [x0, x1, y0, y1] = limites(vertices);
  for (let i = Math.ceil(x0 / escala); i <= Math.floor(x1 / escala); i++) {
    for (let j = Math.ceil(y0 / escala); j <= Math.floor(y1 / escala); j++) {
      if (pointInPolygon({ x: i * escala, y: j * escala }, vertices, 0)) n++;
    }
  }
  return n;
}

function limites(vertices: readonly Vec2[]): [number, number, number, number] {
  const xs = vertices.map((v) => v.x);
  const ys = vertices.map((v) => v.y);
  return [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
}

// ---------------------------------------------------------------------------
// Escrita de coordenadas e da escala
// ---------------------------------------------------------------------------

/** O rótulo do primeiro cruzamento sobre cada eixo: o lado do quadradinho. */
const ROTULO_LADO: Readonly<Record<Denominador, string>> = { 1: '1', 2: '1/2', 3: '1/3' };

/** O lado do quadradinho por extenso, para o enunciado. */
const LADO_TXT: Readonly<Record<Denominador, string>> = {
  1: '1 unidade',
  2: '1/2 unidade',
  3: '1/3 de unidade',
};

/** Uma coordenada: inteiro com o sinal de menos tipográfico, ou fração da malha. */
function coordTxt(x: number, d: Denominador): string {
  const n = Math.round(x * d);
  const sinal = n < 0 ? '−' : '';
  const m = Math.abs(n);
  if (m % d === 0) return `${sinal}${m / d}`;
  const g = gcd(m, d);
  return `${sinal}${m / g}/${d / g}`;
}

const pontoTxt = (q: Vec2, d: Denominador): string => `(${coordTxt(q.x, d)}, ${coordTxt(q.y, d)})`;

/** Pontos separados por ponto e vírgula: a vírgula já separa as coordenadas. */
const listaTxt = (qs: readonly Vec2[], d: Denominador): string =>
  qs.map((q) => pontoTxt(q, d)).join('; ');

// ---------------------------------------------------------------------------
// Cenário: malha quadriculada, eixos, origem e o tamanho do quadradinho
// ---------------------------------------------------------------------------

function quadro(p: Params): Rect {
  const [x0, x1, y0, y1] = limites(p.vertices);
  const folga = 1.5;
  const minX = Math.min(x0, -0.8) - folga;
  const maxX = Math.max(x1, 1.6) + folga;
  const minY = Math.min(y0, -0.8) - folga;
  const maxY = Math.max(y1, 1.6) + folga;
  return {
    x: minX * U,
    y: minY * U,
    w: (maxX - minX) * U,
    h: (maxY - minY) * U,
  };
}

/**
 * A MALHA MOSTRA ONDE MARCAR; SÓ UMA MARCA DIZ QUANTO ELA MEDE.
 *
 * Todas as linhas são iguais — nenhuma é mais forte a cada unidade, porque destacar
 * as linhas inteiras entregaria a resposta. Os eixos param antes da borda, para
 * lerem-se como eixos e não como moldura, e a malha termina exatamente numa linha
 * dela mesma, sem tocos soltos na borda.
 */
function cenario(p: Params): Shape[] {
  const q = quadro(p);
  const recuo = 0.55 * U;
  const ex0 = q.x + recuo;
  const ex1 = q.x + q.w - recuo;
  const ey0 = q.y + recuo;
  const ey1 = q.y + q.h - recuo;

  const passo = U / p.denominador;
  const gx0 = Math.ceil(ex0 / passo - 1e-9) * passo;
  const gx1 = Math.floor(ex1 / passo + 1e-9) * passo;
  const gy0 = Math.ceil(ey0 / passo - 1e-9) * passo;
  const gy1 = Math.floor(ey1 / passo + 1e-9) * passo;

  const rotulo = ROTULO_LADO[p.denominador];
  /** Meio comprimento do traço de escala, atravessando o eixo. */
  const traco = 0.17 * U;

  return [
    { t: 'grid', step: passo, extent: { x: gx0, y: gy0, w: gx1 - gx0, h: gy1 - gy0 }, dots: false },

    // A figura, com preenchimento translúcido: a malha continua visível por dentro.
    {
      t: 'poly',
      pts: p.vertices.map(emTela),
      closed: true,
      role: 'given',
      fill: 'result-fill',
    },

    { t: 'seg', a: { x: ex0, y: 0 }, b: { x: ex1, y: 0 }, role: 'given', arrow: 'end' },
    { t: 'seg', a: { x: 0, y: ey0 }, b: { x: 0, y: ey1 }, role: 'given', arrow: 'end' },
    { t: 'label', at: { x: ex1, y: 0 }, text: [txt('x')], anchor: 'se' },
    { t: 'label', at: { x: 0, y: ey1 }, text: [txt('y')], anchor: 'nw' },

    // A ORIGEM E A ESCALA SÃO TRAÇOS, NÃO PONTOS.
    //
    // Numa versão anterior elas eram bolinhas — exatamente o símbolo de um ponto que o
    // aluno marca. Quando a marca de escala caía dentro da figura, não havia como saber
    // se ela estava marcada ou não; e a origem, que pode ser um dos pontos pedidos,
    // parecia marcada desde o início. Um traço de régua atravessando o eixo não se
    // confunde com marcação nenhuma. O primeiro cruzamento de cada eixo é rotulado com
    // o lado do quadradinho — "1/2", e não "1": é o menor segmento que se anuncia.
    { t: 'label', at: { x: 0, y: 0 }, text: [txt('0')], anchor: 'sw', emphasis: 'tick' },
    { t: 'seg', a: { x: passo, y: -traco }, b: { x: passo, y: traco }, role: 'given' },
    { t: 'label', at: { x: passo, y: -traco }, text: [txt(rotulo)], anchor: 's', emphasis: 'tick' },
    { t: 'seg', a: { x: -traco, y: passo }, b: { x: traco, y: passo }, role: 'given' },
    { t: 'label', at: { x: -traco, y: passo }, text: [txt(rotulo)], anchor: 'w', emphasis: 'tick' },
  ];
}

// ---------------------------------------------------------------------------
// Predicado, diagnósticos e análise
// ---------------------------------------------------------------------------

function marcados(objs: ReadonlyMap<GeoId, GeoObject>): GPoint[] {
  return [...objs.values()].filter(
    (o): o is GPoint => o.kind === 'point' && !GIVEN_IDS.has(o.id as string),
  );
}

/** Coordenadas matemáticas de um ponto de tela. */
const paraMatematicas = (v: Vec2): Vec2 => ({ x: v.x / U, y: v.y / U });

const ehInteiro = (x: number): boolean => Math.abs(x - Math.round(x)) < 1e-6;

const mesmoPonto = (a: Vec2, b: Vec2): boolean =>
  Math.abs(a.x - b.x) < 1e-6 && Math.abs(a.y - b.y) < 1e-6;

function alvoDe(p: Params): ConstructionTarget {
  const esperados = p.resposta.map(emTela);

  const dentro = (v: Vec2): boolean => pointInPolygon(paraMatematicas(v), p.vertices, 0);
  const inteiro = (v: Vec2): boolean => {
    const q = paraMatematicas(v);
    return ehInteiro(q.x) && ehInteiro(q.y);
  };

  /** Separa as marcações do aluno em certas, fora da figura e não inteiras. */
  function classificar(objs: ReadonlyMap<GeoId, GeoObject>): {
    certos: Vec2[];
    fora: Vec2[];
    naoInteiros: Vec2[];
    faltando: Vec2[];
    total: number;
  } {
    const ms = marcados(objs);
    const certos: Vec2[] = [];
    const fora: Vec2[] = [];
    const naoInteiros: Vec2[] = [];
    for (const m of ms) {
      const q = paraMatematicas(m.pos);
      if (!dentro(m.pos)) fora.push(q);
      else if (!inteiro(m.pos)) naoInteiros.push(q);
      else certos.push(q);
    }
    const faltando = p.resposta.filter((r) => !certos.some((c) => mesmoPonto(c, r)));
    return { certos, fora, naoInteiros, faltando, total: ms.length };
  }

  return {
    describe: [
      txt('Marque '),
      strong('todos'),
      txt(' os pontos de coordenadas inteiras que estão dentro da figura — e apenas eles.'),
    ],

    predicate(objs, _givens, tol) {
      return latticeSetEquals(marcados(objs), esperados, tol);
    },

    diagnostics: [
      {
        // Marcou cruzamentos que não têm coordenadas inteiras: leu a malha como se cada
        // quadradinho medisse 1. É o erro que a escala fina existe para revelar.
        error: MALHA_ESCALA_IGNORADA,
        test: (objs) => marcados(objs).some((m) => dentro(m.pos) && !inteiro(m.pos)),
      },
      {
        error: MALHA_INCLUI_FORA,
        test: (objs) => marcados(objs).some((m) => !dentro(m.pos)),
      },
      {
        error: MALHA_INCOMPLETO,
        test: () => true,
      },
    ],

    // Sem construção, não há o que perturbar: a resposta é um conjunto exato.
    robustnessTrials: 0,

    partialCredit: [
      {
        id: 'so-inteiros',
        label: 'Marcou apenas pontos de coordenadas inteiras',
        weight: 0.5,
        test: (objs) => {
          const ms = marcados(objs);
          return ms.length > 0 && ms.every((m) => inteiro(m.pos));
        },
      },
      {
        id: 'metade',
        label: 'Encontrou ao menos metade dos pontos pedidos',
        weight: 0.5,
        test: (objs, _g, tol: Tol) => {
          void tol;
          const ms = marcados(objs).filter((m) => dentro(m.pos) && inteiro(m.pos));
          return ms.length * 2 >= esperados.length;
        },
      },
    ],

    /**
     * O comentário diz ONDE estão os erros, ponto por ponto. "Faltam 2 pontos" obriga o
     * aluno a procurar; "faltaram (0, −1) e (2, 3)" mostra o que ele não enxergou.
     */
    analyze(objs) {
      const c = classificar(objs);
      const d = p.denominador;
      const out: Inline[] = [];

      if (c.total === 0) {
        out.push(txt('Você não marcou nenhum ponto.'));
      } else {
        out.push(
          txt(
            `Você marcou ${conta(c.total, 'ponto')}, e ` +
              `${c.certos.length} ${verbo('estar.pres', numeroDe(c.certos.length))} ${adjetivo('certo', 'm', numeroDe(c.certos.length))}.`,
          ),
        );
      }
      if (c.fora.length > 0) {
        out.push(br, strong('Fora da figura: '), txt(`${listaTxt(c.fora, d)}.`));
      }
      if (c.naoInteiros.length > 0) {
        out.push(
          br,
          strong('Dentro da figura, mas sem as duas coordenadas inteiras: '),
          txt(`${listaTxt(c.naoInteiros, d)}.`),
        );
      }
      if (c.faltando.length > 0) {
        out.push(
          br,
          strong(`${capitalizar(verbo('faltar.pret', numeroDe(c.faltando.length)))}: `),
          txt(`${listaTxt(c.faltando, d)}.`),
        );
      }
      if (c.total > 0 && c.fora.length + c.naoInteiros.length + c.faltando.length === 0) {
        out.push(br, txt(`São exatamente os ${p.resposta.length} pontos pedidos.`));
      }
      return out;
    },

    /** Na revisão: anel de destaque no que está certo, vermelho no que sobrou, tracejado no que faltou. */
    reviewShapes(objs) {
      const raio = 0.27 * U;
      const formas: Shape[] = [];
      const certos: Vec2[] = [];
      for (const m of marcados(objs)) {
        const ok = dentro(m.pos) && inteiro(m.pos);
        if (ok) certos.push(paraMatematicas(m.pos));
        formas.push({ t: 'circle', c: m.pos, r: raio, role: ok ? 'result' : 'error' });
      }
      for (const r of p.resposta) {
        if (certos.some((c) => mesmoPonto(c, r))) continue;
        formas.push({ t: 'circle', c: emTela(r), r: raio, role: 'construction' });
        formas.push({ t: 'point', p: emTela(r), role: 'ghost' });
      }
      return formas;
    },

    reviewLegend: [
      txt('Anel colorido: marcação certa. Anel vermelho: marcação que não deveria estar lá. '),
      txt('Anel tracejado: ponto que faltou marcar.'),
    ],
  };
}

// ---------------------------------------------------------------------------

export const pontosNaMalha: ItemTemplate<Params, ConstructionSnapshot> = {
  id: templateId('h3-07-K01-malha'),
  // v5: origem e escala viram traços de régua, e os pontos marcados perdem as letras.
  version: 5,
  taskModel: taskModelCode('H3.7.K01'),
  skills: [skillCode('H3.7')],
  axis: 'I',
  level: 'N3',
  title: 'Pontos de coordenadas inteiras dentro de uma figura',
  responseModel: '2PL',

  difficultySources: ['steps.2'],

  params: drawParams,
  solve: () => EMPTY_SNAPSHOT,
  buggy: [],

  variantDifficulty(p) {
    const fontes: DifficultySourceCode[] = [];
    // Quadradinho menor que a unidade: o aluno precisa separar cruzamento de inteiro.
    if (p.denominador > 1) fontes.push('select-information');
    if (p.denominador === 3) fontes.push('non-round-numbers');
    if (p.resposta.length >= 7) fontes.push('steps.3plus');
    return fontes;
  },

  build(p) {
    const lado = LADO_TXT[p.denominador];
    const rotulo = ROTULO_LADO[p.denominador];
    const d = p.denominador;

    const desc =
      'Plano cartesiano com os eixos x e y e uma malha quadriculada alinhada aos eixos, em ' +
      `que cada quadradinho tem lado de ${lado}. A origem está indicada com 0, e o primeiro ` +
      `cruzamento da malha sobre cada eixo tem um pequeno traço, rotulado ${rotulo}. Uma figura poligonal de ` +
      `${p.vertices.length} lados, inclinada em relação aos eixos, está destacada. Os pontos ` +
      'podem ser marcados nos cruzamentos da malha. A tarefa é marcar apenas os pontos cujas ' +
      'duas coordenadas são números inteiros e que estão dentro da figura.';

    const cena = scene(cenario(p), {
      a11y: { title: 'Figura no plano cartesiano', desc },
      viewBox: quadro(p),
    });

    return {
      // Sem as desigualdades algébricas: a figura já diz qual é a região, e traduzi-la
      // em sistema de inequações é outro item, de outro momento do currículo.
      stem: [
        txt('No plano cartesiano abaixo, a malha quadriculada acompanha os eixos, e o lado de '),
        txt('cada quadradinho mede '),
        strong(lado),
        txt(' — é o que indica a marcação em cada eixo, logo depois da origem.'),
        br,
        txt('Os pontos só podem ser marcados nos cruzamentos da malha.'),
      ],
      comando: [
        txt('Marque todos os pontos que estão '),
        strong('dentro'),
        txt(' da figura e cujas '),
        strong('duas coordenadas são números inteiros'),
        // Sem aviso sobre o contorno: o gerador já garante que nenhum ponto inteiro fica
        // sobre ele (ver FRONTEIRA, no topo).
        txt('.'),
      ],
      stimuli: [
        {
          kind: 'static-figure',
          motion: 'none',
          audio: { need: 'none' },
          id: 'figura',
          scene: cena,
          alt: desc,
        },
      ],
      interaction: {
        kind: 'construct',
        toolbelt: ['select', 'point', 'delete', 'undo'],
        givens: EMPTY_SNAPSHOT,
        backdrop: cenario(p),
        viewBox: quadro(p),
        perturbable: [],
        target: alvoDe(p),
        snapping: {
          pixelRadius: 18,
          // O clique cai nos cruzamentos da malha — que podem ser mais finos que a
          // unidade. É o que obriga o aluno a distinguir "onde dá para marcar" de "o
          // que deve ser marcado".
          lattice: { on: true, step: U / p.denominador },
          toPoints: true,
          toIntersections: false,
          toObjects: false,
        },
        maxActions: 80,
        // Sem letras nos pontos marcados: com dez pontos, "A, B, C…" só poluem a figura.
        labelCreatedPoints: false,
      },
      rationale: [
        strong('Resolução. '),
        ...(d === 1
          ? [
              txt(
                'Cada quadradinho tem lado 1, então todo cruzamento da malha tem coordenadas ' +
                  'inteiras: basta marcar os que estão dentro da figura.',
              ),
            ]
          : [
              txt(
                `Cada quadradinho tem lado ${rotulo}: são ${d} quadradinhos para avançar 1 ` +
                  `unidade. Por isso, a partir da origem, só a cada ${d} linhas da malha — nas ` +
                  'duas direções — se chega a uma coordenada inteira.',
              ),
            ]),
        br,
        txt(`Os pontos pedidos são ${p.resposta.length}: ${listaTxt(p.resposta, d)}.`),
        ...(d > 1
          ? [
              br,
              txt(
                `Dentro da figura há ${p.marcaveisDentro} cruzamentos da malha, mas só ` +
                  `${p.resposta.length} deles têm as duas coordenadas inteiras; os demais não ` +
                  'entram na resposta.',
              ),
            ]
          : []),
      ],
      declared: p.denominador > 1 ? 'dificil' : 'media',
    };
  },

  validate(_built, p) {
    if (p.resposta.length < 4) return `resposta com apenas ${p.resposta.length} pontos`;
    if (p.denominador > 1 && p.marcaveisDentro <= p.resposta.length) {
      return 'a escala fina não cria distinção: todo ponto marcável é inteiro';
    }
    return null;
  },
};
